import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet, useLocation } from 'react-router';

import { Box } from '@mui/system';
import Container from '@mui/material/Container';
import { Tab, Tabs, Stack, Button, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useParams, useRouter } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';
import { DueMarker } from 'src/services/tasks/tasks.types';
import { useGetMyTasksQuery } from 'src/services/tasks/tasks.service';
import { useGetCallListsQuery } from 'src/services/call-lists/call-lists.service';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { useCallListsEnabled } from 'src/sections/call-lists/hooks/use-call-lists-enabled';

// ----------------------------------------------------------------------

enum TasksPageTab {
  TASKS = 'tasks',
  CALL_LIST = 'call_list',
}

export default function TasksPage() {
  const settings = useSettingsContext();

  const { t } = useTranslate();

  const { task_id } = useParams();

  const router = useRouter();

  const { pathname } = useLocation();

  // JT1-1827: call lists sit beside tasks, behind the same Task Management feature.
  const callListsEnabled = useCallListsEnabled();

  const tab = pathname.replace(/\/$/, '').endsWith('/call-list') ? TasksPageTab.CALL_LIST : TasksPageTab.TASKS;

  const { currentData: dueTasks } = useGetMyTasksQuery({ dueMarker: DueMarker.DUE_TODAY, page: 1, perPage: 1 }, { skip: !callListsEnabled });
  const { currentData: callLists } = useGetCallListsQuery(undefined, { skip: !callListsEnabled });
  const callsDue = useMemo(() => (callLists ?? []).reduce((sum, list) => sum + list.counts.due_today, 0), [callLists]);

  const viewingDetails = useMemo(() => !!task_id, [task_id]);

  const onBack = () => {
    router.back();
  };

  return (
    <>
      <Helmet>
        <title>Dashboard: Tasks</title>
      </Helmet>

      <Container
        maxWidth={settings.themeStretch ? false : 'lg'}
        sx={{
          height: 1,
        }}
      >
        <Stack spacing={1}>
          <CustomBreadcrumbs
            heading={t(`tasks.title`)}
            links={[
              { name: t('common.dashboard'), href: paths.dashboard.root },
              {
                name: t(`tasks.title`),
                href: `${paths.dashboard.tasks.root()}`
              },
              ...(tab === TasksPageTab.CALL_LIST ? [{ name: t('call_lists.title') }] : []),
            ]}
          />

          {callListsEnabled && !viewingDetails && (
            <Tabs
              value={tab}
              onChange={(_event, next: TasksPageTab) => router.push(
                next === TasksPageTab.CALL_LIST ? paths.dashboard.tasks.callList() : paths.dashboard.tasks.root()
              )}
              sx={{ mb: 1 }}
            >
              <Tab
                value={TasksPageTab.TASKS}
                iconPosition="end"
                icon={<Label variant="soft">{dueTasks?.count ?? 0}</Label>}
                label={<Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{t('tasks.tabs.tasks')}</Typography>}
              />
              <Tab
                value={TasksPageTab.CALL_LIST}
                iconPosition="end"
                icon={<Label variant="soft" color={callsDue ? 'primary' : 'default'}>{t('call_lists.due_count', { count: callsDue })}</Label>}
                label={<Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{t('call_lists.title')}</Typography>}
                data-testid="tasks-tab-call-list"
              />
            </Tabs>
          )}

          {viewingDetails && (
            <Box>
              <Button
                fullWidth={false}
                component={RouterLink}
                onClick={onBack}
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={16} />}
              >
                {t('common.back')}
              </Button>
            </Box>
          )}

          <Outlet />
        </Stack>
      </Container>
    </>
  );
}
